// @ts-nocheck
/**
 * FX取引管理システム（v3.0 リファクタリング版）
 *
 * 【設計変更】
 * - FXレートはカード価格（属性指数）から計算される
 * - ニュース→FXレートの直接適用を廃止
 * - レート = base属性指数 / quote属性指数
 *
 * 設計: market_economy_system_v3.md
 */

import { FX_PAIRS, getPairVolatility, getRiskCategory } from '../data/fxPairs';
import { FX, INVESTMENT } from '../../data/constants';
import { hasDebt } from './debtManager';
import {
  getAttributeIndex,
  calculateFXRate as calcFXRate,
  getFXChangeRate,
} from '../../market/fundCalculator';

// ========================================
// レート計算（fundCalculatorへのデリゲート）
// ========================================

/**
 * FXレートを計算（属性指数の比率）
 *
 * @param {string} pairId - ペアID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} FXレート
 */
export function calculateFXRate(pairId, marketIndex, marketState) {
  const pair = FX_PAIRS[pairId];
  if (!pair) return 1.0;

  if (!marketIndex || !marketState) {
    // フォールバック: 初期レート1.0
    return 1.0;
  }

  return calcFXRate(pair.base, pair.quote, marketIndex, marketState);
}

/**
 * 全ペアのFXレートを計算
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} { pairId: rate }
 */
export function calculateAllFXRates(marketIndex, marketState) {
  const rates = {};
  for (const pairId of Object.keys(FX_PAIRS)) {
    rates[pairId] = calculateFXRate(pairId, marketIndex, marketState);
  }
  return rates;
}

/**
 * FXレートの初期化
 *
 * @returns {Object} 初期レート（全て1.0）
 */
export function initializeFXRates() {
  const rates = {};
  for (const pairId of Object.keys(FX_PAIRS)) {
    rates[pairId] = 1.0;
  }
  return rates;
}

/**
 * FXレートの変動率を取得（%）
 * 基準レート1.0からの乖離
 *
 * @param {string} pairId - ペアID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 変動率（%）
 */
export function getFXRateChangeFromBase(pairId, marketIndex, marketState) {
  const pair = FX_PAIRS[pairId];
  if (!pair) return 0;

  if (!marketIndex || !marketState) {
    return 0;
  }

  return getFXChangeRate(pair.base, pair.quote, marketIndex, marketState);
}

// ========================================
// レート履歴管理
// ========================================

/**
 * 日次レート履歴を記録
 *
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} 更新された投資データ
 */
export function recordDailyFXRates(investmentData, marketIndex, marketState) {
  // 現在のレートを計算（カード価格から）
  const currentRates = calculateAllFXRates(marketIndex, marketState);

  // レート履歴を更新
  const newRateHistory = { ...investmentData.fxRateHistory };
  for (const [pairId, rate] of Object.entries(currentRates)) {
    if (!newRateHistory[pairId]) {
      newRateHistory[pairId] = [];
    }
    newRateHistory[pairId].push(rate);
    // 最新30件に制限
    if (newRateHistory[pairId].length > 30) {
      newRateHistory[pairId] = newRateHistory[pairId].slice(-30);
    }
  }

  return {
    ...investmentData,
    fxRates: currentRates,
    fxRateHistory: newRateHistory,
  };
}

/**
 * FXレートの前日比変動率を取得（%）
 *
 * @param {Object} investmentData - 投資データ
 * @param {string} pairId - ペアID
 * @returns {number} 変動率（%）
 */
export function getFXRateChange(investmentData, pairId) {
  const history = investmentData.fxRateHistory && investmentData.fxRateHistory[pairId];
  if (!history || history.length < 2) return 0;

  const currentRate = history[history.length - 1];
  const previousRate = history[history.length - 2];

  if (previousRate === 0) return 0;
  return ((currentRate - previousRate) / previousRate) * 100;
}

// ========================================
// 後方互換性のための関数（非推奨）
// ========================================

/**
 * @deprecated FXレートはカード価格から自動計算されます
 */
export function processDailyFXRateUpdate(investmentData, marketState, marketIndex = null) {
  console.warn('processDailyFXRateUpdate() is deprecated. Use recordDailyFXRates() instead.');

  if (!marketIndex) {
    return investmentData;
  }

  return recordDailyFXRates(investmentData, marketIndex, marketState);
}

// ========================================
// ポジション管理
// ========================================

/**
 * FXポジションを開く（検証）
 *
 * @param {Object} investmentData - 投資データ
 * @param {number} gold - 所持金
 * @param {string} pairId - ペアID
 * @param {string} direction - 'long' | 'short'
 * @param {number} margin - 証拠金
 * @param {number} leverage - レバレッジ倍率
 * @returns {Object} { valid, error?, cost?, spread? }
 */
export function validateOpenFXPosition(investmentData, gold, pairId, direction, margin, leverage = 1) {
  const pair = FX_PAIRS[pairId];
  if (!pair) {
    return { valid: false, error: '存在しないペアです' };
  }

  if (margin < 1000) {
    return { valid: false, error: '最低証拠金は1,000Gです' };
  }

  // 借金中は投資不可
  if (hasDebt(investmentData.debt)) {
    return { valid: false, error: '借金を返済してから投資してください' };
  }

  // レバレッジ解放チェック
  const unlockedLeverages = getUnlockedLeverages(investmentData.experienceCount);
  if (!unlockedLeverages.includes(leverage)) {
    return { valid: false, error: `レバレッジ${leverage}倍は解放されていません` };
  }

  // ペアリスクとレバレッジの確認（警告のみ、購入は許可）
  const pairVola = getPairVolatility(pairId);
  const risk = getRiskCategory(pairVola);
  const leverageWarning = leverage > risk.maxLeverage
    ? `⚠️ このペアの推奨最大レバレッジは${risk.maxLeverage}倍です。高リスク取引となります。`
    : null;

  // スプレッドコスト
  const spreadCost = Math.floor(margin * FX.SPREAD_RATE);
  const totalCost = margin + spreadCost;

  if (gold < totalCost) {
    return { valid: false, error: '所持金が不足しています', cost: totalCost, spread: spreadCost };
  }

  return { valid: true, cost: totalCost, spread: spreadCost, margin, warning: leverageWarning };
}

/**
 * FXポジションを開く
 *
 * @param {Object} investmentData - 投資データ
 * @param {string} pairId - ペアID
 * @param {string} direction - 'long' | 'short'
 * @param {number} margin - 証拠金
 * @param {number} leverage - レバレッジ倍率
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @param {Object} options - オプション（stopLoss, takeProfit）
 * @returns {Object} { newInvestmentData, cost, spread }
 */
export function openFXPosition(investmentData, pairId, direction, margin, leverage, currentBattleNumber, marketIndex, marketState, options = {}) {
  // 現在レートをカード価格から計算
  const entryRate = calculateFXRate(pairId, marketIndex, marketState);
  const spreadCost = Math.floor(margin * FX.SPREAD_RATE);
  const totalCost = margin + spreadCost;

  // ロスカットレートを計算
  const liquidationThreshold = INVESTMENT.LIQUIDATION_THRESHOLD;
  const liquidationRate = direction === 'long'
    ? entryRate * (1 - liquidationThreshold / leverage)
    : entryRate * (1 + liquidationThreshold / leverage);

  const newPosition = {
    id: `fx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    pair: pairId,  // サーバーと統一: pair フィールドを使用
    direction,
    leverage,
    margin,
    entryRate,
    liquidationRate,
    swapPaid: 0,
    openedAt: currentBattleNumber,
    stopLoss: options.stopLoss || null,
    takeProfit: options.takeProfit || null,
    notifyOnTrigger: options.notifyOnTrigger !== false,
  };

  const newPositions = [...investmentData.fxPositions, newPosition];

  // 取引履歴に追加
  const historyEntry = {
    date: currentBattleNumber,
    action: 'open_fx',
    type: 'fx',
    pair: pairId,
    direction,
    margin,
    leverage,
    entryRate,
    spread: spreadCost,
  };
  const newHistory = [...investmentData.history, historyEntry].slice(-100);

  // 統計更新
  const newStats = {
    ...investmentData.totalStats,
    totalInvestments: investmentData.totalStats.totalInvestments + 1,
    totalInvestedAmount: investmentData.totalStats.totalInvestedAmount + margin,
    totalFeesPaid: investmentData.totalStats.totalFeesPaid + spreadCost,
  };

  return {
    newInvestmentData: {
      ...investmentData,
      fxPositions: newPositions,
      history: newHistory,
      experienceCount: investmentData.experienceCount + 1,
      totalStats: newStats,
    },
    cost: totalCost,
    spread: spreadCost,
    position: newPosition,
  };
}

/**
 * FXポジションを閉じる
 *
 * @param {Object} investmentData - 投資データ
 * @param {string} positionId - ポジションID
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} { newInvestmentData, pnl, returnAmount }
 */
export function closeFXPosition(investmentData, positionId, currentBattleNumber, marketIndex, marketState) {
  const position = investmentData.fxPositions.find((p) => p.id === positionId);
  if (!position) {
    return { newInvestmentData: investmentData, pnl: 0, returnAmount: 0, error: 'ポジションが見つかりません' };
  }

  // pair フィールドに統一（旧データの pairId もフォールバックでサポート）
  const pairId = position.pair || position.pairId;

  // 現在レートをカード価格から計算
  const currentRate = calculateFXRate(pairId, marketIndex, marketState);
  const { pnl, pnlPercent } = calculateFXPositionPnL(position, currentRate);

  // 返還金額（証拠金 + 損益 - スワップ）
  const returnAmount = Math.max(0, position.margin + pnl - position.swapPaid);

  // ポジションを削除
  const newPositions = investmentData.fxPositions.filter((p) => p.id !== positionId);

  // 取引履歴に追加
  const historyEntry = {
    date: currentBattleNumber,
    action: 'close_fx',
    type: 'fx',
    pair: pairId,
    direction: position.direction,
    margin: position.margin,
    leverage: position.leverage,
    entryRate: position.entryRate,
    exitRate: currentRate,
    pnl,
    pnlPercent,
    swapPaid: position.swapPaid,
  };
  const newHistory = [...investmentData.history, historyEntry].slice(-100);

  // 統計更新
  const newStats = {
    ...investmentData.totalStats,
    totalProfitableTrades: investmentData.totalStats.totalProfitableTrades + (pnl > 0 ? 1 : 0),
    totalPayout: investmentData.totalStats.totalPayout + returnAmount,
    totalProfit: investmentData.totalStats.totalProfit + pnl,
  };

  return {
    newInvestmentData: {
      ...investmentData,
      fxPositions: newPositions,
      history: newHistory,
      totalStats: newStats,
    },
    pnl,
    pnlPercent,
    returnAmount,
    position,
  };
}

// ========================================
// 損益計算
// ========================================

/**
 * FXポジションの損益を計算
 *
 * @param {Object} position - ポジション
 * @param {number} currentRate - 現在レート
 * @returns {Object} { pnl, pnlPercent }
 */
export function calculateFXPositionPnL(position, currentRate) {
  const { entryRate, margin, leverage, direction } = position;

  let pnlPercent;
  if (direction === 'long') {
    // ロング: レート上昇で利益
    pnlPercent = ((currentRate - entryRate) / entryRate) * 100 * leverage;
  } else {
    // ショート: レート下落で利益
    pnlPercent = ((entryRate - currentRate) / entryRate) * 100 * leverage;
  }

  const pnl = Math.floor(margin * pnlPercent / 100);

  return { pnl, pnlPercent };
}

/**
 * FXポジションの詳細情報を取得
 *
 * @param {Object} position - ポジション
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} 詳細情報
 */
export function getFXPositionDetails(position, investmentData, currentBattleNumber, marketIndex, marketState) {
  // pair フィールドに統一（旧データの pairId もフォールバックでサポート）
  const pairId = position.pair || position.pairId;
  const pair = FX_PAIRS[pairId];
  const currentRate = calculateFXRate(pairId, marketIndex, marketState);
  const { pnl, pnlPercent } = calculateFXPositionPnL(position, currentRate);
  const pairVola = getPairVolatility(pairId);
  const risk = getRiskCategory(pairVola);
  const daysOpen = currentBattleNumber - position.openedAt;
  const swapPaid = position.swapPaid || 0;  // サーバーから同期時に未設定の場合のフォールバック

  // ロスカットまでの余裕
  let liquidationMargin;
  // liquidationRate が未設定の場合はデフォルト計算
  const liquidationRate = position.liquidationRate || (
    position.direction === 'long'
      ? position.entryRate * (1 - INVESTMENT.LIQUIDATION_THRESHOLD / position.leverage)
      : position.entryRate * (1 + INVESTMENT.LIQUIDATION_THRESHOLD / position.leverage)
  );
  if (position.direction === 'long') {
    liquidationMargin = ((currentRate - liquidationRate) / currentRate) * 100;
  } else {
    liquidationMargin = ((liquidationRate - currentRate) / currentRate) * 100;
  }

  return {
    ...position,
    pair,
    currentRate,
    pnl,
    pnlPercent,
    risk,
    daysOpen,
    liquidationMargin,
    isNearLiquidation: liquidationMargin < 5,
    estimatedValue: position.margin + pnl - swapPaid,
  };
}

// ========================================
// スワップポイント
// ========================================

/**
 * 日次スワップポイントを計算・適用
 *
 * @param {Object} investmentData - 投資データ
 * @returns {Object} { newInvestmentData, totalSwap, swapDetails }
 */
export function applyDailySwap(investmentData) {
  let totalSwap = 0;
  const swapDetails = [];

  const newPositions = investmentData.fxPositions.map((position) => {
    const positionValue = position.margin * position.leverage;
    const swapCost = Math.floor(positionValue * FX.SWAP_RATE);
    totalSwap += swapCost;

    // サーバーは 'pair'、ローカルは 'pairId' を使用するため両方対応
    const pairId = position.pairId || position.pair;
    swapDetails.push({
      positionId: position.id,
      pairId,
      swapCost,
    });

    return {
      ...position,
      swapPaid: position.swapPaid + swapCost,
    };
  });

  return {
    newInvestmentData: {
      ...investmentData,
      fxPositions: newPositions,
      totalStats: {
        ...investmentData.totalStats,
        totalFeesPaid: investmentData.totalStats.totalFeesPaid + totalSwap,
      },
    },
    totalSwap,
    swapDetails,
  };
}

// ========================================
// ロスカット処理
// ========================================

/**
 * 夜間変動がFXペアに影響するか判定
 *
 * @param {string} pairId - ペアID
 * @param {Object} overnightVolatility - 夜間変動情報
 * @returns {boolean} 影響するかどうか
 */
function isPositionAffectedByOvernight(pairId, overnightVolatility) {
  if (!overnightVolatility || !overnightVolatility.targets) {
    return false;
  }

  const pair = FX_PAIRS[pairId];
  if (!pair) return false;

  // 夜間変動の対象に、このペアのbase/quote属性が含まれているか
  return overnightVolatility.targets.some((target) => {
    // 属性ターゲット
    if (target.attribute) {
      return target.attribute === pair.base || target.attribute === pair.quote;
    }
    return false;
  });
}

/**
 * 夜間スパイクを適用したレートを計算
 *
 * @param {string} pairId - ペアID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @param {Object} overnightVolatility - 夜間変動情報
 * @returns {number} スパイク適用後のレート
 */
function calculateRateWithOvernightSpike(pairId, marketIndex, marketState, overnightVolatility) {
  const baseRate = calculateFXRate(pairId, marketIndex, marketState);

  if (!overnightVolatility || !isPositionAffectedByOvernight(pairId, overnightVolatility)) {
    return baseRate;
  }

  // スパイクを適用（spikePercentは正または負の値）
  const spikeMultiplier = 1 + (overnightVolatility.spikePercent / 100);
  return baseRate * spikeMultiplier;
}

/**
 * ロスカット対象のポジションを検出（夜間変動考慮）
 *
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @param {Object} [overnightVolatility=null] - 夜間変動情報
 * @returns {Array} ロスカット対象ポジション
 */
export function detectLiquidationPositions(investmentData, marketIndex, marketState, overnightVolatility = null) {
  return investmentData.fxPositions.filter((position) => {
    // サーバーは 'pair'、ローカルは 'pairId' を使用するため両方対応
    const pairId = position.pairId || position.pair;
    // 夜間変動がある場合はスパイク適用レートで判定
    const effectiveRate = overnightVolatility
      ? calculateRateWithOvernightSpike(pairId, marketIndex, marketState, overnightVolatility)
      : calculateFXRate(pairId, marketIndex, marketState);

    if (position.direction === 'long') {
      return effectiveRate <= position.liquidationRate;
    } else {
      return effectiveRate >= position.liquidationRate;
    }
  });
}

/**
 * ロスカットを実行（夜間変動考慮）
 *
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @param {Object} [overnightVolatility=null] - 夜間変動情報
 * @returns {Object} { newInvestmentData, liquidatedPositions, totalLoss, debtGenerated, overnightLiquidations }
 */
export function processLiquidations(investmentData, currentBattleNumber, marketIndex, marketState, overnightVolatility = null) {
  const liquidationTargets = detectLiquidationPositions(investmentData, marketIndex, marketState, overnightVolatility);

  if (liquidationTargets.length === 0) {
    return {
      newInvestmentData: investmentData,
      liquidatedPositions: [],
      totalLoss: 0,
      debtGenerated: 0,
      overnightLiquidations: [],
    };
  }

  let newInvestmentData = { ...investmentData };
  const liquidatedPositions = [];
  const overnightLiquidations = []; // 夜間変動によるロスカット
  let totalLoss = 0;
  let debtGenerated = 0;

  for (const position of liquidationTargets) {
    // サーバーは 'pair'、ローカルは 'pairId' を使用するため両方対応
    const pairId = position.pairId || position.pair;

    const result = closeFXPosition(newInvestmentData, position.id, currentBattleNumber, marketIndex, marketState);
    newInvestmentData = result.newInvestmentData;

    const loss = Math.abs(Math.min(0, result.pnl));
    totalLoss += loss;

    // 損失が証拠金を超える場合は借金発生
    if (result.pnl < -position.margin) {
      const debt = Math.abs(result.pnl) - position.margin;
      debtGenerated += debt;
    }

    const liquidationInfo = {
      ...position,
      pnl: result.pnl,
      exitRate: calculateFXRate(pairId, marketIndex, marketState),
    };

    liquidatedPositions.push(liquidationInfo);

    // 夜間変動によるロスカットかどうかを判定
    if (overnightVolatility && isPositionAffectedByOvernight(pairId, overnightVolatility)) {
      // 通常レートではロスカットされないが、夜間スパイクでロスカットされた場合
      const normalRate = calculateFXRate(pairId, marketIndex, marketState);
      const wouldLiquidateNormally = position.direction === 'long'
        ? normalRate <= position.liquidationRate
        : normalRate >= position.liquidationRate;

      if (!wouldLiquidateNormally) {
        overnightLiquidations.push({
          ...liquidationInfo,
          spikePercent: overnightVolatility.spikePercent,
          spikeDirection: overnightVolatility.direction,
        });
      }
    }
  }

  return {
    newInvestmentData,
    liquidatedPositions,
    totalLoss,
    debtGenerated,
    overnightLiquidations,
  };
}

// ========================================
// ヘルパー関数
// ========================================

/**
 * 解放済みレバレッジを取得
 *
 * @param {number} experience - 投資経験回数
 * @returns {Array<number>} 解放済みレバレッジ
 */
function getUnlockedLeverages(experience) {
  return INVESTMENT.LEVERAGE_OPTIONS.filter((lev) => {
    const required = INVESTMENT.LEVERAGE_UNLOCK[lev];
    return required !== undefined && experience >= required;
  });
}

/**
 * 全ペアの詳細情報を取得
 *
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Array} ペア詳細一覧
 */
export function getAllFXPairsWithDetails(investmentData, marketIndex, marketState) {
  return Object.entries(FX_PAIRS).map(([pairId, pair]) => {
    const currentRate = calculateFXRate(pairId, marketIndex, marketState);
    const rateChange = getFXRateChange(investmentData, pairId);
    const pairVola = getPairVolatility(pairId);
    const risk = getRiskCategory(pairVola);

    // このペアのポジション数
    const positionCount = investmentData.fxPositions.filter((p) => p.pairId === pairId).length;

    return {
      ...pair,
      currentRate,
      rateChange,
      volatility: pairVola,
      ...risk,
      positionCount,
      isHot: rateChange >= 3,
      isCold: rateChange <= -3,
    };
  });
}

// ========================================
// エクスポート
// ========================================

export default {
  // 新API
  calculateFXRate,
  calculateAllFXRates,
  initializeFXRates,
  getFXRateChangeFromBase,
  recordDailyFXRates,
  getFXRateChange,
  // ポジション管理
  validateOpenFXPosition,
  openFXPosition,
  closeFXPosition,
  calculateFXPositionPnL,
  getFXPositionDetails,
  // スワップ・ロスカット
  applyDailySwap,
  detectLiquidationPositions,
  processLiquidations,
  getAllFXPairsWithDetails,
  // 後方互換性（非推奨）
  processDailyFXRateUpdate,
};
